import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Divider
} from '@mui/material';
import {
  ExpandMore,
  CheckCircle,
  PlayArrow,
  Dashboard,
  Inventory,
  People,
  Business,
  AccountBalance,
  Settings,
  Webhook,
  Add,
  Edit,
  Delete,
  Key,
  Pause,
  PlayArrowOutlined,
  ArrowForward
} from '@mui/icons-material';

export const UsageGuidePage = () => {
  const [expanded, setExpanded] = useState<string | false>('overview');

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          使い方ガイド
        </Typography>
        <Typography variant="body1" color="text.secondary">
          認証システム管理画面の使い方をステップバイステップで説明します
        </Typography>
      </Box>

      {/* 概要 */}
      <Accordion expanded={expanded === 'overview'} onChange={handleChange('overview')}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Dashboard color="primary" />
            <Typography variant="h6">システム概要</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 3 }}>
            このシステムは、複数のプロダクト（アプリケーション）のライセンス管理と、
            ホワイトラベル/OEMパートナー（テナント）の管理を一元化するプラットフォームです。
          </Alert>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            主な機能
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><Inventory color="primary" /></ListItemIcon>
              <ListItemText
                primary="プロダクト管理"
                secondary="販売するアプリケーション（Gambit、MFMなど）とプランの管理"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><People color="primary" /></ListItemIcon>
              <ListItemText
                primary="ユーザー管理"
                secondary="エンドユーザーのアカウント作成、ライセンス付与、契約管理"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Business color="primary" /></ListItemIcon>
              <ListItemText
                primary="テナント管理"
                secondary="ホワイトラベル/OEMパートナーの管理、収益分配設定"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><AccountBalance color="primary" /></ListItemIcon>
              <ListItemText
                primary="収益管理"
                secondary="売上分析、パートナー別収益レポート"
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            システム構成
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
            <Chip label="運営者（Super Admin）" color="error" />
            <ArrowForward sx={{ alignSelf: 'center' }} />
            <Chip label="テナント（パートナー）" color="warning" />
            <ArrowForward sx={{ alignSelf: 'center' }} />
            <Chip label="エンドユーザー" color="success" />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ダッシュボード */}
      <Accordion expanded={expanded === 'dashboard'} onChange={handleChange('dashboard')}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Dashboard color="primary" />
            <Typography variant="h6">ダッシュボード</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            ダッシュボードでは、システム全体の状況を一目で確認できます。
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="総ユーザー数・アクティブユーザー数" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="プロダクト別の契約状況" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="今月の売上サマリー" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="最近のアクティビティ" />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* プロダクト管理 */}
      <Accordion expanded={expanded === 'products'} onChange={handleChange('products')}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Inventory color="primary" />
            <Typography variant="h6">プロダクト管理</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            販売するアプリケーション（プロダクト）とそのプランを管理します。
          </Typography>

          <Stepper orientation="vertical">
            <Step active>
              <StepLabel>プロダクトの確認</StepLabel>
              <StepContent>
                <Typography>
                  「プロダクト管理」メニューを開くと、登録済みのプロダクト一覧が表示されます。
                  各プロダクトには複数のプラン（Free、Basic、Proなど）が設定できます。
                </Typography>
              </StepContent>
            </Step>
            <Step active>
              <StepLabel>プランの確認</StepLabel>
              <StepContent>
                <Typography>
                  各プランには以下の情報が含まれます：
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="プラン名（Free, Basic, Pro, Enterprise）" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="価格（月額）" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="最大デバイス数" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="利用可能な機能" />
                  </ListItem>
                </List>
              </StepContent>
            </Step>
          </Stepper>
        </AccordionDetails>
      </Accordion>

      {/* ユーザー管理 */}
      <Accordion expanded={expanded === 'users'} onChange={handleChange('users')}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <People color="primary" />
            <Typography variant="h6">ユーザー管理</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            エンドユーザーのアカウント作成、ライセンス付与、契約管理を行います。
          </Typography>

          <Card sx={{ mb: 2, bgcolor: 'grey.50' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                <Add sx={{ verticalAlign: 'middle', mr: 1 }} />
                新規ユーザーを作成する
              </Typography>
              <Stepper orientation="vertical">
                <Step active>
                  <StepLabel>「ユーザー作成」ボタンをクリック</StepLabel>
                  <StepContent>
                    <Typography>画面右上の「ユーザー作成」ボタンをクリックします。</Typography>
                  </StepContent>
                </Step>
                <Step active>
                  <StepLabel>基本情報を入力</StepLabel>
                  <StepContent>
                    <List dense>
                      <ListItem><ListItemText primary="メールアドレス（必須）" /></ListItem>
                      <ListItem><ListItemText primary="氏名（任意）" /></ListItem>
                      <ListItem><ListItemText primary="パスワード（空欄で自動生成）" /></ListItem>
                    </List>
                  </StepContent>
                </Step>
                <Step active>
                  <StepLabel>役割を選択</StepLabel>
                  <StepContent>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="一般ユーザー"
                          secondary="通常のエンドユーザー"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="テナント管理者"
                          secondary="パートナー企業の管理者（テナント選択が必要）"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="管理者"
                          secondary="システム全体の管理者"
                        />
                      </ListItem>
                    </List>
                  </StepContent>
                </Step>
                <Step active>
                  <StepLabel>プロダクト・プランを選択</StepLabel>
                  <StepContent>
                    <Typography>
                      ユーザーに付与するプロダクト（Gambit等）とプラン（Basic, Pro等）を選択します。
                      契約期限も設定できます。
                    </Typography>
                  </StepContent>
                </Step>
                <Step active>
                  <StepLabel>作成完了</StepLabel>
                  <StepContent>
                    <Typography>
                      「作成」ボタンをクリックすると、ユーザーが作成され、
                      設定したメールアドレスに認証情報が送信されます。
                    </Typography>
                  </StepContent>
                </Step>
              </Stepper>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: 'grey.50' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                <Edit sx={{ verticalAlign: 'middle', mr: 1 }} />
                ユーザー情報を編集する
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><Key /></ListItemIcon>
                  <ListItemText
                    primary="ライセンストークン再生成"
                    secondary="鍵アイコンをクリックすると新しいトークンが発行されます"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Edit /></ListItemIcon>
                  <ListItemText
                    primary="ユーザー情報編集"
                    secondary="鉛筆アイコンでプラン・期限などを変更できます"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Delete /></ListItemIcon>
                  <ListItemText
                    primary="ユーザー削除"
                    secondary="ゴミ箱アイコンでユーザーを削除します（復元不可）"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </AccordionDetails>
      </Accordion>

      {/* テナント管理 */}
      <Accordion expanded={expanded === 'tenants'} onChange={handleChange('tenants')}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Business color="primary" />
            <Typography variant="h6">テナント管理（パートナー管理）</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 3 }}>
            テナントとは、ホワイトラベル/OEMパートナー企業のことです。
            各テナントは独自のブランディングでプロダクトを販売でき、収益を分配できます。
          </Alert>

          <Card sx={{ mb: 2, bgcolor: 'grey.50' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                <Add sx={{ verticalAlign: 'middle', mr: 1 }} />
                新規テナントを作成する
              </Typography>
              <Stepper orientation="vertical">
                <Step active>
                  <StepLabel>「テナント作成」ボタンをクリック</StepLabel>
                  <StepContent />
                </Step>
                <Step active>
                  <StepLabel>基本情報を入力</StepLabel>
                  <StepContent>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="テナントコード"
                          secondary="半角英数字とハイフンのみ（例: acme, partner-a）"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="テナント名"
                          secondary="会社名や表示名"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="オーナーメールアドレス"
                          secondary="パートナー企業の担当者メール"
                        />
                      </ListItem>
                    </List>
                  </StepContent>
                </Step>
                <Step active>
                  <StepLabel>作成完了・APIキー発行</StepLabel>
                  <StepContent>
                    <Typography>
                      テナント作成時にAPIキーとWebhookシークレットが発行されます。
                      これらは一度しか表示されないため、必ず安全な場所に保管してください。
                    </Typography>
                  </StepContent>
                </Step>
              </Stepper>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2, bgcolor: 'grey.50' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                <Edit sx={{ verticalAlign: 'middle', mr: 1 }} />
                テナント詳細を編集する
              </Typography>
              <Typography paragraph>
                テナント一覧で「編集」アイコン（鉛筆）をクリックすると、詳細編集画面が開きます。
              </Typography>

              <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>
                基本情報タブ
              </Typography>
              <List dense>
                <ListItem><ListItemText primary="テナント名・オーナー情報の編集" /></ListItem>
                <ListItem><ListItemText primary="サブドメイン・カスタムドメイン設定" /></ListItem>
                <ListItem><ListItemText primary="プラン選択（Basic / Pro / Enterprise）" /></ListItem>
              </List>

              <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>
                課金・制限タブ
              </Typography>
              <List dense>
                <ListItem><ListItemText primary="課金モデル（収益分配 / 固定 / ライセンス）" /></ListItem>
                <ListItem><ListItemText primary="最大ユーザー数・プロダクト数の制限" /></ListItem>
                <ListItem><ListItemText primary="許可する機能の選択" /></ListItem>
              </List>

              <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>
                プロダクト・収益分配タブ（重要）
              </Typography>
              <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
                ここで各プロダクトの収益分配率を設定します
              </Alert>
              <Stepper orientation="vertical">
                <Step active>
                  <StepLabel>「プロダクト追加」ボタンをクリック</StepLabel>
                  <StepContent />
                </Step>
                <Step active>
                  <StepLabel>プロダクトを選択</StepLabel>
                  <StepContent>
                    <Typography>テナントが販売できるプロダクト（Gambit等）を選択します。</Typography>
                  </StepContent>
                </Step>
                <Step active>
                  <StepLabel>許可するプランを選択</StepLabel>
                  <StepContent>
                    <Typography>テナントが販売できるプラン（Free, Basic, Pro等）を選択します。</Typography>
                  </StepContent>
                </Step>
                <Step active>
                  <StepLabel>収益分配先を設定</StepLabel>
                  <StepContent>
                    <Typography paragraph>
                      「分配先を追加」で収益の分配先と割合を設定します。
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'white' }}>
                      <Typography variant="body2">例：</Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText
                            primary="運営（admin@example.com）: 30%"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="パートナー（partner@acme.com）: 70%"
                          />
                        </ListItem>
                      </List>
                      <Typography variant="caption" color="error">
                        ※ 合計が100%になるように設定してください
                      </Typography>
                    </Paper>
                  </StepContent>
                </Step>
              </Stepper>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: 'grey.50' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                その他の操作
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><Key /></ListItemIcon>
                  <ListItemText
                    primary="APIキー再生成"
                    secondary="セキュリティ上の理由でキーを変更する場合"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Pause /></ListItemIcon>
                  <ListItemText
                    primary="一時停止"
                    secondary="テナントのサービスを一時的に停止（停止理由を入力）"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><PlayArrowOutlined /></ListItemIcon>
                  <ListItemText
                    primary="再開"
                    secondary="停止中のテナントを再開"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Delete /></ListItemIcon>
                  <ListItemText
                    primary="削除"
                    secondary="テナントを完全に削除（復元不可・関連データも削除）"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </AccordionDetails>
      </Accordion>

      {/* 収益管理 */}
      <Accordion expanded={expanded === 'revenue'} onChange={handleChange('revenue')}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AccountBalance color="primary" />
            <Typography variant="h6">収益管理</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            売上の分析とパートナー別の収益レポートを確認できます。
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText
                primary="月別売上推移"
                secondary="グラフで売上のトレンドを確認"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText
                primary="プロダクト別売上"
                secondary="どのプロダクトが売れているかを分析"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText
                primary="テナント別収益"
                secondary="パートナーごとの売上と分配額を確認"
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* システム設定 */}
      <Accordion expanded={expanded === 'settings'} onChange={handleChange('settings')}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Settings color="primary" />
            <Typography variant="h6">システム設定</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            システム全体の設定を管理します。
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText
                primary="メール設定"
                secondary="SMTPサーバー、送信元アドレスなど"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText
                primary="認証設定"
                secondary="トークン有効期限、パスワードポリシーなど"
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Webhook設定 */}
      <Accordion expanded={expanded === 'webhook'} onChange={handleChange('webhook')}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Webhook color="primary" />
            <Typography variant="h6">Webhook設定ガイド</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" paragraph>
            決済サービス（Stripe）からのWebhook通知を受け取るための設定ガイドです。
            テナントにこの情報を共有して、決済連携を設定してもらいます。
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            詳細は「Webhook設定ガイド」メニューを参照してください
          </Alert>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText
                primary="Webhook URLの設定"
                secondary="Stripeダッシュボードで設定するエンドポイントURL"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText
                primary="イベントの選択"
                secondary="checkout.session.completed, customer.subscription.* など"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText
                primary="署名検証"
                secondary="Webhook Secretを使用した署名の検証"
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* よくある操作フロー */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          よくある操作フロー
        </Typography>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <PlayArrow sx={{ verticalAlign: 'middle', mr: 1, color: 'primary.main' }} />
              新規パートナーを追加して収益分配を設定する
            </Typography>
            <Stepper orientation="vertical">
              <Step active>
                <StepLabel>テナント管理 → 「テナント作成」</StepLabel>
                <StepContent>
                  <Typography>パートナー企業の基本情報を入力してテナントを作成</Typography>
                </StepContent>
              </Step>
              <Step active>
                <StepLabel>APIキーを保存</StepLabel>
                <StepContent>
                  <Typography>発行されたAPIキーをパートナーに共有</Typography>
                </StepContent>
              </Step>
              <Step active>
                <StepLabel>テナント詳細編集を開く</StepLabel>
                <StepContent>
                  <Typography>作成したテナントの編集アイコンをクリック</Typography>
                </StepContent>
              </Step>
              <Step active>
                <StepLabel>「プロダクト・収益分配」タブを開く</StepLabel>
                <StepContent>
                  <Typography>3つ目のタブをクリック</Typography>
                </StepContent>
              </Step>
              <Step active>
                <StepLabel>プロダクトを追加</StepLabel>
                <StepContent>
                  <Typography>販売を許可するプロダクトを選択し、収益分配率を設定</Typography>
                </StepContent>
              </Step>
              <Step active>
                <StepLabel>テナント管理者ユーザーを作成</StepLabel>
                <StepContent>
                  <Typography>ユーザー管理で「テナント管理者」役割のユーザーを作成し、作成したテナントを選択</Typography>
                </StepContent>
              </Step>
            </Stepper>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <PlayArrow sx={{ verticalAlign: 'middle', mr: 1, color: 'primary.main' }} />
              エンドユーザーにライセンスを付与する
            </Typography>
            <Stepper orientation="vertical">
              <Step active>
                <StepLabel>ユーザー管理 → 「ユーザー作成」</StepLabel>
                <StepContent>
                  <Typography>ユーザーのメールアドレスを入力</Typography>
                </StepContent>
              </Step>
              <Step active>
                <StepLabel>役割を「一般ユーザー」に設定</StepLabel>
                <StepContent />
              </Step>
              <Step active>
                <StepLabel>プロダクトとプランを選択</StepLabel>
                <StepContent>
                  <Typography>例: Gambit / Pro</Typography>
                </StepContent>
              </Step>
              <Step active>
                <StepLabel>契約期限を設定（任意）</StepLabel>
                <StepContent>
                  <Typography>期限なしの場合は空欄のままでOK</Typography>
                </StepContent>
              </Step>
              <Step active>
                <StepLabel>作成完了</StepLabel>
                <StepContent>
                  <Typography>ユーザーにメールが送信され、ライセンスが付与されます</Typography>
                </StepContent>
              </Step>
            </Stepper>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
